/**
 * Cache Service for HR Portal
 * 
 * Provides localStorage-based caching with:
 * - User-scoped data (keyed by email)
 * - TTL (time-to-live) support
 * - Version management for cache invalidation
 * - LRU eviction when storage is full
 * - Stale-while-revalidate pattern support
 */

import { getEmail } from './auth';

// Cache configuration
const CACHE_PREFIX = 'appli_hr_cache';
const CACHE_VERSION = 1; // Increment to invalidate all caches
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const MAX_CACHE_SIZE = 4 * 1024 * 1024; // 4MB limit (leave room for other localStorage usage)

/**
 * Generate a cache key for the current user
 * @param {string} dataKey - The key identifying the data type (e.g., 'dashboard', 'jobPositions')
 * @param {string} [resourceId] - Optional resource ID for specific items
 * @returns {string} Full cache key
 */
export const getCacheKey = (dataKey, resourceId = null) => {
    const email = getEmail();
    if (!email) return null;
    
    // Sanitize email for use in key
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    
    if (resourceId) {
        return `${CACHE_PREFIX}_v${CACHE_VERSION}_${sanitizedEmail}_${dataKey}_${resourceId}`;
    }
    return `${CACHE_PREFIX}_v${CACHE_VERSION}_${sanitizedEmail}_${dataKey}`;
};

/**
 * Cache entry structure
 * @typedef {Object} CacheEntry
 * @property {*} data - The cached data
 * @property {number} timestamp - When the data was cached
 * @property {number} ttl - Time-to-live in milliseconds
 * @property {number} version - Cache version
 * @property {string} dataKey - The data key for this entry
 */

/**
 * Set data in cache
 * @param {string} dataKey - The key identifying the data type
 * @param {*} data - The data to cache
 * @param {Object} options - Cache options
 * @param {string} [options.resourceId] - Optional resource ID
 * @param {number} [options.ttl] - Time-to-live in milliseconds (default: 5 minutes)
 * @returns {boolean} Whether the cache was set successfully
 */
export const setCache = (dataKey, data, options = {}) => {
    const { resourceId = null, ttl = DEFAULT_TTL } = options;
    
    const key = getCacheKey(dataKey, resourceId);
    if (!key) return false;
    
    const entry = {
        data,
        timestamp: Date.now(),
        ttl,
        version: CACHE_VERSION,
        dataKey
    };
    
    try {
        const serialized = JSON.stringify(entry);
        
        // Check if we need to evict old entries
        if (serialized.length > MAX_CACHE_SIZE) {
            console.warn(`[Cache] Data too large to cache: ${dataKey}`);
            return false;
        }
        
        // Try to set, evict if needed
        try {
            localStorage.setItem(key, serialized);
        } catch (e) {
            if (e.name === 'QuotaExceededError') {
                evictOldEntries();
                localStorage.setItem(key, serialized);
            } else {
                throw e;
            }
        }
        
        // Update access order for LRU
        updateAccessOrder(key);
        
        return true;
    } catch (error) {
        console.error(`[Cache] Error setting cache for ${dataKey}:`, error);
        return false;
    }
};

/**
 * Get data from cache
 * @param {string} dataKey - The key identifying the data type
 * @param {Object} options - Cache options
 * @param {string} [options.resourceId] - Optional resource ID
 * @param {boolean} [options.allowStale] - Return stale data if available (default: true)
 * @returns {{ data: *, isStale: boolean, timestamp: number } | null} Cached data or null
 */
export const getCache = (dataKey, options = {}) => {
    const { resourceId = null, allowStale = true } = options;
    
    const key = getCacheKey(dataKey, resourceId);
    if (!key) return null;
    
    try {
        const serialized = localStorage.getItem(key);
        if (!serialized) return null;
        
        const entry = JSON.parse(serialized);
        
        // Check version
        if (entry.version !== CACHE_VERSION) {
            localStorage.removeItem(key);
            return null;
        }
        
        const age = Date.now() - entry.timestamp;
        const isStale = age > entry.ttl;
        
        // Update access order for LRU
        updateAccessOrder(key);
        
        if (isStale && !allowStale) {
            return null;
        }
        
        return {
            data: entry.data,
            isStale,
            timestamp: entry.timestamp,
            age
        };
    } catch (error) {
        console.error(`[Cache] Error getting cache for ${dataKey}:`, error);
        // Remove corrupted entry
        const key = getCacheKey(dataKey, resourceId);
        if (key) localStorage.removeItem(key);
        return null;
    }
};

/**
 * Check if cache exists and is fresh
 * @param {string} dataKey - The key identifying the data type
 * @param {Object} options - Cache options
 * @param {string} [options.resourceId] - Optional resource ID
 * @returns {boolean} Whether fresh cache exists
 */
export const hasFreshCache = (dataKey, options = {}) => {
    const result = getCache(dataKey, { ...options, allowStale: false });
    return result !== null;
};

/**
 * Invalidate (remove) specific cache entry
 * @param {string} dataKey - The key identifying the data type
 * @param {string} [resourceId] - Optional resource ID
 */
export const invalidateCache = (dataKey, resourceId = null) => {
    const key = getCacheKey(dataKey, resourceId);
    if (key) {
        localStorage.removeItem(key);
        removeFromAccessOrder(key);
    }
};

/**
 * Invalidate all cache entries for a specific data type
 * @param {string} dataKey - The key identifying the data type to invalidate
 */
export const invalidateCacheByType = (dataKey) => {
    const email = getEmail();
    if (!email) return;
    
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const prefix = `${CACHE_PREFIX}_v${CACHE_VERSION}_${sanitizedEmail}_${dataKey}`;
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keysToRemove.push(key);
        }
    }
    
    keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        removeFromAccessOrder(key);
    });
};

/**
 * Clear all cache for current user
 */
export const clearUserCache = () => {
    const email = getEmail();
    if (!email) return;
    
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const prefix = `${CACHE_PREFIX}_v${CACHE_VERSION}_${sanitizedEmail}`;
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keysToRemove.push(key);
        }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear access order for user
    localStorage.removeItem(`${CACHE_PREFIX}_access_order_${sanitizedEmail}`);
};

/**
 * Clear ALL cache (all users) - useful for version upgrades
 */
export const clearAllCache = () => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
            keysToRemove.push(key);
        }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
};

/**
 * Get cache statistics
 * @returns {Object} Cache statistics
 */
export const getCacheStats = () => {
    const email = getEmail();
    if (!email) return { entries: 0, size: 0, staleCount: 0 };
    
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const prefix = `${CACHE_PREFIX}_v${CACHE_VERSION}_${sanitizedEmail}`;
    
    let entries = 0;
    let size = 0;
    let staleCount = 0;
    
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix) && !key.includes('access_order')) {
            entries++;
            const value = localStorage.getItem(key);
            size += value ? value.length : 0;
            
            try {
                const entry = JSON.parse(value);
                if (Date.now() - entry.timestamp > entry.ttl) {
                    staleCount++;
                }
            } catch (e) {
                // Ignore parse errors
            }
        }
    }
    
    return {
        entries,
        size,
        sizeKB: (size / 1024).toFixed(2),
        staleCount,
        maxSize: MAX_CACHE_SIZE,
        maxSizeKB: (MAX_CACHE_SIZE / 1024).toFixed(2)
    };
};

// ============ LRU Eviction Helpers ============

/**
 * Get access order array for current user
 */
const getAccessOrder = () => {
    const email = getEmail();
    if (!email) return [];
    
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const key = `${CACHE_PREFIX}_access_order_${sanitizedEmail}`;
    
    try {
        const order = localStorage.getItem(key);
        return order ? JSON.parse(order) : [];
    } catch {
        return [];
    }
};

/**
 * Update access order (move key to end = most recently used)
 */
const updateAccessOrder = (cacheKey) => {
    const email = getEmail();
    if (!email) return;
    
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const orderKey = `${CACHE_PREFIX}_access_order_${sanitizedEmail}`;
    
    try {
        let order = getAccessOrder();
        order = order.filter(k => k !== cacheKey);
        order.push(cacheKey);
        localStorage.setItem(orderKey, JSON.stringify(order));
    } catch {
        // Ignore errors
    }
};

/**
 * Remove key from access order
 */
const removeFromAccessOrder = (cacheKey) => {
    const email = getEmail();
    if (!email) return;
    
    const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
    const orderKey = `${CACHE_PREFIX}_access_order_${sanitizedEmail}`;
    
    try {
        let order = getAccessOrder();
        order = order.filter(k => k !== cacheKey);
        localStorage.setItem(orderKey, JSON.stringify(order));
    } catch {
        // Ignore errors
    }
};

/**
 * Evict oldest entries when storage is full
 */
const evictOldEntries = () => {
    const order = getAccessOrder();
    
    // Remove oldest 20% of entries
    const toRemove = Math.max(1, Math.floor(order.length * 0.2));
    const keysToRemove = order.slice(0, toRemove);
    
    keysToRemove.forEach(key => {
        localStorage.removeItem(key);
    });
    
    // Update access order
    const email = getEmail();
    if (email) {
        const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, '_');
        const orderKey = `${CACHE_PREFIX}_access_order_${sanitizedEmail}`;
        const newOrder = order.slice(toRemove);
        localStorage.setItem(orderKey, JSON.stringify(newOrder));
    }
    
    console.log(`[Cache] Evicted ${keysToRemove.length} old entries`);
};

// ============ Predefined Cache Keys ============
// Use these constants for consistency across the app

export const CACHE_KEYS = {
    // Dashboard
    JOB_POSITIONS: 'jobPositions',
    ALL_NOTIFICATIONS: 'allNotifications',
    APPLICATION_COUNTS: 'applicationCounts',
    WORK_TRIAL_COUNTS: 'workTrialCounts',
    JOB_POSTING_ACTIVE: 'jobPostingActive',
    POSITION_DETAILS: 'positionDetails', // Use with resourceId
    
    // Job Posting page
    JOB_POSTING: 'jobPosting', // Use with job_position_id
    JOB_POSITION_INFO: 'jobPositionInfo', // Use with job_position_id
    
    // Future pages (add as needed)
    USER_PROFILE: 'userProfile',
    SETTINGS: 'settings',
    CANDIDATES: 'candidates',
    INTERVIEWS: 'interviews',
};

// ============ TTL Presets ============

export const TTL = {
    SHORT: 1 * 60 * 1000,      // 1 minute - for rapidly changing data
    MEDIUM: 5 * 60 * 1000,     // 5 minutes - default
    LONG: 15 * 60 * 1000,      // 15 minutes - for slower changing data
    VERY_LONG: 60 * 60 * 1000, // 1 hour - for rarely changing data
};

const cacheUtils = {
    setCache,
    getCache,
    hasFreshCache,
    invalidateCache,
    invalidateCacheByType,
    clearUserCache,
    clearAllCache,
    getCacheStats,
    getCacheKey,
    CACHE_KEYS,
    TTL
};

export default cacheUtils;

